import {
  BellNotificationModel,
  NotificationSeverity,
} from '../../api/models/NotificationCenterModel';
import { IconsService } from '../../services/icons.service';

/**
 * Shared helpers of the notification center (panel of the bell,
 * preferences). Icons come from IconsService only: one concept = one icon.
 */
export class NotificationCenterTools {
  private static readonly SEVERITIES: NotificationSeverity[] = [
    'INFO',
    'SUCCESS',
    'WARNING',
    'ERROR',
  ];

  /**
   * Kind of record (data_model.object) → key of IconsService, so a
   * notification about an order shows the icon of the orders everywhere else.
   */
  private static readonly OBJECT_ICON_KEYS: { [object: string]: string } = {
    ORDER: 'ORDER',
    ORDERMAIN: 'ORDER',
    PROPOSAL: 'PROPOSAL',
    INVOICE: 'INVOICE',
    EVENT: 'EVENT',
    CLIENT: 'CLIENT',
    ENTERPRISE: 'ENTERPRISE',
    EMPLOYEE: 'EMPLOYEE',
    PRODUCT: 'PRODUCT',
    MATERIAL: 'MATERIAL',
    PLACE: 'PLACE',
    ROOM: 'ROOM',
    SHOP: 'SHOP',
    PROJECT: 'PROJECT',
    CONGRESS: 'CONGRESS',
    CONGRESSGROUP: 'GROUP_ENTERPRISE',
    SUBMISSION: 'SUBMISSION',
  };

  /** Unknown or missing severity reads as INFO. */
  static severity(value: string | null | undefined): NotificationSeverity {
    const severity = String(value ?? '').toUpperCase() as NotificationSeverity;
    return NotificationCenterTools.SEVERITIES.includes(severity)
      ? severity
      : 'INFO';
  }

  /**
   * Icon of one notification, always a Material icon of IconsService:
   * 1. `icon` sent by the API when it names a key of IconsService;
   * 2. the icon of the record the notification points to;
   * 3. the icon of the severity.
   */
  static icon(notification: BellNotificationModel): string {
    const fromApi = NotificationCenterTools.iconOfKey(notification.icon);
    if (fromApi) {
      return fromApi;
    }

    const model = notification.data_model;
    if (model?.object) {
      // An Order carries its document type (PROPOSAL / ORDER / INVOICE).
      const byType = NotificationCenterTools.iconOfObject(model['type']);
      if (byType) {
        return byType;
      }
      const byObject = NotificationCenterTools.iconOfObject(model.object);
      if (byObject) {
        return byObject;
      }
    }

    return NotificationCenterTools.severityIcon(notification.severity);
  }

  static severityIcon(value: string | null | undefined): string {
    // INFO, SUCCESS, WARNING and ERROR are keys of IconsService.
    return IconsService.getIconStatic(NotificationCenterTools.severity(value));
  }

  private static iconOfObject(value: unknown): string | null {
    if (typeof value !== 'string' || value === '') {
      return null;
    }
    const key =
      NotificationCenterTools.OBJECT_ICON_KEYS[
        value.replace(/[^a-zA-Z]/g, '').toUpperCase()
      ];
    return key ? NotificationCenterTools.iconOfKey(key) : null;
  }

  /** Silent lookup (IconsService.getIconStatic logs an error when missing). */
  private static iconOfKey(key: string | null | undefined): string | null {
    if (!key) {
      return null;
    }
    const icon = (IconsService as any)[String(key).toUpperCase()];
    // FontAwesome icons need fontSet="fa": the list renders Material only.
    return typeof icon === 'string' && !icon.startsWith('fa-') ? icon : null;
  }
}
